import Table from '../../../components/table/table';
import { applicationData } from '../../../types/interfaces';
import { formatCurrency } from '../../../utils/Masks';
import { ACTIVO_SIN_COTIZACION_LABEL } from '../../../utils/quotationAssets';
import { useEffect, useState } from 'react';
import { getProductLines } from '../../../services/ClientService';
import {
  parseSelectableProductLines,
  resolveSelectableProductLineLabel,
  UNAVAILABLE_PRODUCT_LINE_LABEL,
  type SelectableProductLine,
} from '../../../utils/productLines';
import Span from '../../../components/texts/span/Span';
import TruncatedTextWithTooltip from '../../../components/TruncatedTextWithTooltip/TruncatedTextWithTooltip';
import styled from 'styled-components';

const ResponsiveTableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    -webkit-overflow-scrolling: touch;
  }
  
  /* Estilos para las tablas internas */
  .rdt_Table {
    width: 100%;
    min-width: 100%;
    table-layout: auto;
    
    @media (max-width: 768px) {
      min-width: 100%;
    }
    
    @media (max-width: 480px) {
      min-width: 100%;
    }
  }
  
  .rdt_TableCol {
    min-width: 0;
    flex: 1;
    
    @media (max-width: 768px) {
      min-width: 0;
    }
    
    @media (max-width: 480px) {
      min-width: 0;
    }
  }
`;

interface ApplicationProps {
  applications: applicationData[];
}

const SummaryApplication: React.FC<ApplicationProps> = ({
  applications: applications,
}) => {
  const [productLines, setProductLines] = useState<SelectableProductLine[]>();
  const [availableAssets, setAvailableAssets] = useState<any[]>([]);
  const tableData = {
    size: applications.length,
    totalElements: applications.length,
  };

  const config = JSON.parse(localStorage.getItem('config') ?? '{}');
  // isQuotationsEnabled viene de application.configuration.is_quotations_enabled: si es false, no se muestran Activo ni Cantidad
  const isQuotationsEnabled = config?.isQuotationsEnabled ?? false;

  const hasAssets = applications.some((app) => app.has_assets);

  useEffect(() => {
    if (!productLines) {
      getProductLines().then((response) => {
        if (response && 'data' in response) {
          setProductLines(parseSelectableProductLines(response.data));
        }
      });
    }
  }, [productLines]);

  useEffect(() => {
    if (isQuotationsEnabled && hasAssets) {
      fetch('/v1/private/applications/quotations', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      })
        .then((res) => res.json())
        .then((data) => {
          // Handle both array and object with data property
          const assets = Array.isArray(data) ? data : (data?.data || []);
          setAvailableAssets(assets);
        })
        .catch((error) => {
          console.error('Error loading quotations:', error);
          setAvailableAssets([]);
        });
    }
  }, [isQuotationsEnabled, hasAssets]);

  const columns = [
    {
      name: 'Producto',
      selector: (row: any) => {
        const label =
          productLines && row.product_line_code
            ? resolveSelectableProductLineLabel(productLines, row.product_line_code) ??
              UNAVAILABLE_PRODUCT_LINE_LABEL
            : '';
        return (
          <TruncatedTextWithTooltip text={label}>
            <Span type='3' variant='neutral'>{label}</Span>
          </TruncatedTextWithTooltip>
        );
      },
      grow: 1,
    },
    ...(isQuotationsEnabled
      ? [
          {
            name: 'Activo',
            selector: (row: any) => {
              const assetLabel = (() => {
                if (!row.asset_code) return ACTIVO_SIN_COTIZACION_LABEL;
                if (!Array.isArray(availableAssets)) return row.asset_code;
                return (
                  availableAssets.find((item: any) => item.code === row.asset_code)?.name ||
                  row.asset_code
                );
              })();
              return (
                <TruncatedTextWithTooltip text={assetLabel}>
                  <Span type='3' variant='neutral'>{assetLabel}</Span>
                </TruncatedTextWithTooltip>
              );
            },
            grow: 1,
          },
          {
            name: 'Cantidad',
            selector: (row: any) => (
              <Span type='3' variant='neutral'>
                {row.asset_count ?? '-'}
              </Span>
            ),
            grow: 0.5,
          },
        ]
      : []),
    {
      name: 'Moneda',
      selector: (row: any) => (
        <Span type='3' variant='neutral'>
          {row.amount.currency_code}
        </Span>
      ),
      grow: 1,
    },
    {
      name: 'Monto',
      selector: (row: any) => (
        <Span type='3' variant='neutral'>
          $ {formatCurrency(row.amount.value ?? 0, true)}
        </Span>
      ),
      grow: 1.0,
      right: true,
    },
  ];

  return (
    <ResponsiveTableWrapper>
      <Table
        columns={columns}
        data={applications}
        tableData={tableData}
        variant='weak'
      />
    </ResponsiveTableWrapper>
  );
};

export default SummaryApplication;
